import {
  organizationSchema,
  useBrandConfig,
  websiteSchema,
} from '@denqfabrik/landing-ui';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGS, type SupportedLang } from '@/i18n/LanguageProvider';
import { convertPath } from '@/i18n/routes';

const OG_LOCALE: Record<SupportedLang, string> = {
  en: 'en_US',
  de: 'de_DE',
  it: 'it_IT',
  es: 'es_ES',
  fr: 'fr_FR',
};

const OG_IMAGE_BY_EN_PATH: Record<string, string> = {
  '/how-it-works': '/og/og-how-it-works.png',
  '/pricing': '/og/og-pricing.png',
};

function resolveOgImage(suffix: string, lang: SupportedLang): string {
  if (suffix === '/') return '/og/og-home.png';
  const enPath = (convertPath(suffix, lang, 'en') || suffix).replace(/\/$/, '');
  return OG_IMAGE_BY_EN_PATH[enPath] ?? '/og/og-default.png';
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): HTMLMetaElement {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function upsertLink(rel: string, extraAttrs: Record<string, string>): HTMLLinkElement {
  const parts = [`link[rel="${rel}"]`];
  for (const [k, v] of Object.entries(extraAttrs)) {
    if (k !== 'href') parts[0] += `[${k}="${v}"]`;
  }
  let el = document.querySelector(parts[0]) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    for (const [k, v] of Object.entries(extraAttrs)) {
      if (k !== 'href') el.setAttribute(k, v);
    }
    document.head.appendChild(el);
  }
  if (extraAttrs.href) el.setAttribute('href', extraAttrs.href);
  return el;
}

function detectLang(pathname: string): SupportedLang {
  const seg = pathname.split('/')[1];
  if ((SUPPORTED_LANGS as readonly string[]).includes(seg)) return seg as SupportedLang;
  return 'de';
}

function stripLang(pathname: string): string {
  const m = pathname.match(/^\/(en|de|it|es|fr)(\/.*)?$/);
  return m ? (m[2] || '/') : pathname;
}

export default function SeoHead() {
  const config = useBrandConfig();
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const lang = detectLang(pathname);
    const suffix = stripLang(pathname);

    const canonicalPath = suffix === '/' ? `/${lang}` : `/${lang}${suffix}`;
    const isLegal = /\/(impressum|datenschutz|imprint|privacy|privacidad|privacy-policy|confidentialite|note-legali|aviso-legal|mentions-legales)/.test(pathname);
    const isHome = /^\/(en|de|it|es|fr)\/?$/.test(pathname) || pathname === '/';

    document.documentElement.lang = lang;

    const ogType = upsertMeta('property', 'og:type', 'website');
    const ogUrl = upsertMeta('property', 'og:url', `${config.baseUrl}${canonicalPath}`);
    const ogLocale = upsertMeta('property', 'og:locale', OG_LOCALE[lang]);
    const ogSiteName = upsertMeta('property', 'og:site_name', config.siteName);
    const ogImagePath = resolveOgImage(suffix, lang);
    const ogImage = upsertMeta('property', 'og:image', `${config.baseUrl}${ogImagePath}`);
    const ogImageW = upsertMeta('property', 'og:image:width', '1200');
    const ogImageH = upsertMeta('property', 'og:image:height', '630');
    const twitterCard = upsertMeta('name', 'twitter:card', 'summary_large_image');
    const twitterImage = upsertMeta('name', 'twitter:image', `${config.baseUrl}${ogImagePath}`);

    const canonical = upsertLink('canonical', { href: `${config.baseUrl}${canonicalPath}` });

    const hreflangLinks: HTMLLinkElement[] = [];
    for (const target of SUPPORTED_LANGS) {
      const converted = convertPath(suffix, lang, target);
      const path = converted === '/' ? `/${target}` : `/${target}${converted}`;
      hreflangLinks.push(upsertLink('alternate', {
        hreflang: target,
        href: `${config.baseUrl}${path}`,
      }));
    }
    const xDefault = upsertLink('alternate', {
      hreflang: 'x-default',
      href: `${config.baseUrl}${suffix === '/' ? '/en' : `/en${convertPath(suffix, lang, 'en')}`}`,
    });

    const scripts: HTMLScriptElement[] = [];
    if (!isLegal) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(organizationSchema(config));
      document.head.appendChild(s);
      scripts.push(s);
    }
    if (isHome) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(websiteSchema(config));
      document.head.appendChild(s);
      scripts.push(s);
    }

    return () => {
      ogType.remove();
      ogUrl.remove();
      ogLocale.remove();
      ogSiteName.remove();
      ogImage.remove();
      ogImageW.remove();
      ogImageH.remove();
      twitterCard.remove();
      twitterImage.remove();
      canonical.remove();
      for (const l of hreflangLinks) l.remove();
      xDefault.remove();
      for (const s of scripts) s.remove();
    };
  }, [config, location.pathname]);

  return null;
}
